import React, { ChangeEvent, useCallback } from 'react';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Grid } from '@mui/material';
import { discounts, DiscountTypes } from 'api';
import { FormCard, FormInput } from 'components';
import DISCOUNTS from 'constants/discounts';
import { AutocompleteSearchItem } from 'api/types/common';
import { dataInitialSelectsOptions } from '../../../fields';

interface Props {
  control: Control<DiscountTypes.ItemCreateParams> | undefined;
  watch: UseFormWatch<DiscountTypes.ItemCreateParams>;
  setValue: UseFormSetValue<DiscountTypes.ItemCreateParams>;
  initialSelectsOptions:
    | ReturnType<typeof dataInitialSelectsOptions>
    | undefined;
}

const EligibilityBlock = (props: Props): JSX.Element | null => {
  const { control, watch, setValue, initialSelectsOptions } = props;

  const brand = watch('brand');
  const countryTeam = watch('country_team');
  const shopId = watch('shop_id');
  const customerEligibility = watch('customer_eligibility');

  const handleCustomerEligibilityChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      onChange: (value: string | string[] | undefined) => void,
    ) => {
      setValue('customer_segments', []);
      setValue('customers_individual', []);

      onChange(event.target.value);
    },
    [setValue],
  );

  const handleAutocompleteChange = useCallback(
    (
      value: AutocompleteSearchItem | AutocompleteSearchItem[] | null,
      onChange: (value: string | string[] | null) => void,
    ) => {
      if (value && Array.isArray(value)) {
        const newValue = value.map((v) => {
          if ('originData' in v) {
            return v.originData;
          }
          return {
            id: v._id.$oid,
            title: v.name,
            image: v.image,
          };
        });

        onChange(newValue);
      }
    },
    [],
  );

  const getCustomersSegments = useCallback(
    (query: string) =>
      discounts
        .autocompleteCustomerSegments({
          customer_list_name: query,
          brand,
          shop_id: shopId,
          country_team: countryTeam,
        })
        .then((res) =>
          res.data.segments.edges.map((e) => ({
            name: e.node.name,
            _id: { $oid: e.node.id },
            originData: {
              id: e.node.id,
              name: e.node.name,
            },
          })),
        ),
    [brand, countryTeam, shopId],
  );

  const getCustomersIndividual = useCallback(
    (query: string) =>
      discounts
        .autocompleteCustomerEmails({
          customer_email: query,
          brand,
          shop_id: shopId,
          country_team: countryTeam,
        })
        .then((res) =>
          res.data.customers.edges.map((e) => ({
            name: e.node.email,
            _id: { $oid: e.node.id },
            originData: e.node,
          })),
        ),
    [brand, countryTeam, shopId],
  );

  return (
    <FormCard title="Eligibility">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormInput
            control={control}
            name="customer_eligibility"
            inputType="radio"
            label="Customer eligibility"
            tooltip={`After changing this field "Customers" and "Customer segments" fields will be reset`}
            options={DISCOUNTS.CUSTOMER_ELIGIBILITY_OPTIONS}
            onChange={handleCustomerEligibilityChange}
            rules={{ required: true }}
          />
        </Grid>
        {customerEligibility !==
          DiscountTypes.CustomerEligibilityEnum.Everyone && (
          <Grid item xs={12}>
            {customerEligibility ===
              DiscountTypes.CustomerEligibilityEnum
                .SpecificCustomerSegments && (
              <FormInput
                control={control}
                name="customer_segments"
                inputType="autocompleteSearch"
                label="Customer segments"
                multiple
                disabled={!shopId}
                initialValue={initialSelectsOptions?.customerSegments}
                onChange={handleAutocompleteChange}
                queryKey={discounts.endpoints.autocompleteCustomerSegments}
                queryFn={getCustomersSegments}
                rules={{
                  required:
                    customerEligibility ===
                    DiscountTypes.CustomerEligibilityEnum
                      .SpecificCustomerSegments,
                }}
              />
            )}
            {customerEligibility ===
              DiscountTypes.CustomerEligibilityEnum.SpecificCustomers && (
              <FormInput
                control={control}
                name="customers_individual"
                inputType="autocompleteSearch"
                label="Customers"
                multiple
                disabled={!shopId}
                initialValue={initialSelectsOptions?.customersIndividual}
                onChange={handleAutocompleteChange}
                queryKey={discounts.endpoints.autocompleteCustomerEmails}
                queryFn={getCustomersIndividual}
                rules={{
                  required:
                    customerEligibility ===
                    DiscountTypes.CustomerEligibilityEnum.SpecificCustomers,
                }}
              />
            )}
          </Grid>
        )}
      </Grid>
    </FormCard>
  );
};

export default EligibilityBlock;
